import { Injectable } from "@angular/core";
import { HttpService } from '@/app/Data/services/Networking/HttpService';
import { DatabaseService } from '@/app/Data/services/Database/database.service';
import { SnackbarService } from '@/app/Utilities/snackbar/snackbar.service';

import { Observable, pluck, map, tap, firstValueFrom, from } from 'rxjs';

import { environment } from '@/environments/environment';
import { SuccessApiResponse } from '@/app/Data/services/Networking/ApiResponse';
import { HttpRequestMethod } from '@/app/Data/services/Networking/HttpRequestMethod';
import { Filters } from '@/app/core/Models/filters';
import { liveQuery } from "dexie";

interface FilterItem {
    module: string;
    category: string;
    values: any[];
}

interface CreateFilterPayload {
    name: string;
    filter_items: FilterItem[];
}

interface UpdateFilterPayload {
    name?: string;
    is_deleted?: boolean;
    filter_items?: Array<{ is_deleted: boolean, module: string, values: Array<any> }>
}

@Injectable({
    providedIn: 'root'
})
export class FilterViewModel {

    constructor(
        private httpService: HttpService,
        private databaseService: DatabaseService,
        private snackBarService: SnackbarService
    ) { }

    createFilter(payload: CreateFilterPayload): Observable<Filters> {
        return this.createFilterViaAPI(payload).pipe(
            tap((response) => {
                this.saveFilterLocally(response);
                this.showSnackbar('Filter created successfully!', 'success');
            })
        );
    }

    updateFilter(filterGroupId: number, payload: UpdateFilterPayload): Observable<Filters> {
        return this.updateFilterViaAPI(filterGroupId, payload).pipe(
            tap((response) => {
                this.saveFilterLocally(response);
                this.showSnackbar('Filter updated successfully!', 'success');
            })
        );
    }

    activateFilterForModule(filterGroupId: number, currentModule: string): Observable<any> {
        return this.activateFilterForModuleViaAPI(filterGroupId, currentModule).pipe(
            tap(async (res) => {
                const filters = await firstValueFrom(this.getFiltersLocally());
                let _filters = filters.map((item) => {
                    if (item.filter_group_id === filterGroupId) {
                        item = { ...item, is_active_tasks: res['results']['activated_filter_group']['is_active_tasks'] };
                    } else {
                        item = { ...item, is_active_tasks: false };
                    }
                    return item;
                })
                this.saveFiltersLocallyBulk(_filters);
            })
        );
    }

    /**
     * Deactivates the active filter for a specific module in the backend database.
     * This ensures no saved filter is auto-loaded on page refresh.
     * Falls back to local-only deactivation if backend call fails.
     */
    deactivateFilterForModule(currentModule: string): Observable<any> {
        return this.deactivateFilterForModuleViaAPI(currentModule).pipe(
            tap(async () => {
                // Also clear local database
                await this.deactivateAllFiltersForTasksLocally();
            })
        );
    }

    /**
     * Deactivates all filters for the tasks module in the local database.
     * This clears the is_active_tasks flag from all filters so that
     * no saved filter is auto-loaded on page refresh.
     */
    async deactivateAllFiltersForTasksLocally(): Promise<void> {
        const filters = await firstValueFrom(this.getFiltersLocally());
        const _filters = filters.map((item) => {
            return { ...item, is_active_tasks: false };
        });
        await this.saveFiltersLocallyBulk(_filters);
    }

    getAllFilters(): Observable<Filters[]> {
        return this.getAllFiltersViaAPI().pipe(
            tap((filters) => {
                let localFilters = filters.map((item) => {
                    return { ...item, actionEnabled: false, assigned_to_all: false, assigned_to_me: false };
                });
                this.saveFiltersLocallyBulk(localFilters);
            })
        );
    }

    getFilterById(filterGroupId: number): Observable<Filters> {
        return this.getFilterByIdViaAPI(filterGroupId);
    }

    getActiveFilterForModule(currentModule: string): Observable<Filters | null> {
        return this.getActiveFilterForModuleViaAPI(currentModule);
    }

    getFiltersLocally() {
        return from(
            liveQuery(() => {
                return this.databaseService.filters.toArray();
            })
        )
    }

    getActiveFilterTasksLocally() {
        return from(
            liveQuery(() => {
                return this.databaseService.filters
                    .filter((item: any) => {
                        if (item.is_active_tasks) {
                            return item;
                        }
                    }).toArray();
            })
        )
    }
    getActiveFilterTasksValuesLocally() {
        return from(
            liveQuery(() => {
                return this.databaseService.filters
                    .filter((item: any) => {
                        let a: any = {};
                        if (item.is_active_tasks) {
                            item.forEach((item2: any) => {
                                item2.filter_items((item3: any) => {
                                    if (item3.category === 'Assignees') {
                                        if (item3.values.length) {
                                            const assigneeIds = item3.values.map((v: any) => v['user_id']);
                                            a = { ...a, assignees: assigneeIds }
                                        }
                                    }
                                    if (item3.category === 'Status') {
                                        if (item3.value.length) {
                                            const statusValues = item3.values.map((v: any) => v['status'])
                                            a = { ...a, status: statusValues }
                                        }

                                    }
                                    if (item3.category === 'Task Priority') {
                                        if (item3.value.length) {
                                            const priorityValues = item3.values.map((v: any) => v['task_priority']);
                                            a = { ...a, priority: priorityValues }
                                        }

                                    }
                                    if (item3.category === 'Tags') {
                                        if (item3.value.length) {
                                            const tagIds = item3.values.map((v: any) => v['tag_id']);
                                            a = { ...a, tags: tagIds }

                                        }

                                    }
                                    if (item3.category === 'Due Date') {
                                        if (item3.value.length) {
                                            const timestamp = item3.values[0]['deadline'];
                                            a = { ...a, due_date: timestamp };
                                        }
                                    }
                                })
                            })
                        }
                        return a;
                    }).toArray();
            })
        )
    }

    getFilterValues(filer: Filters[]) {
        let a = {}
        filer.forEach((item2: Filters) => {
            item2.filter_items.forEach((item3: FilterItem) => {
                if (item3.category === 'Assignees') {
                    if (item3.values.length) {
                        const assigneeIds = item3.values.map((v: any) => v['user_id']);
                        a = { ...a, assignees: assigneeIds }
                    }
                }
                if (item3.category === 'Status') {
                    if (item3.values.length) {
                        const statusValues = item3.values.map((v: any) => v['status'])
                        a = { ...a, status: statusValues }
                    }

                }
                if (item3.category === 'Task Priority') {
                    if (item3.values.length) {
                        const priorityValues = item3.values.map((v: any) => v['task_priority']);
                        a = { ...a, priority: priorityValues }
                    }

                }
                if (item3.category === 'Tags') {
                    if (item3.values.length) {
                        const tagIds = item3.values.map((v: any) => v['tag_id']);
                        a = { ...a, tags: tagIds }

                    }

                }
                if (item3.category === 'Due Date') {
                    if (item3.values.length) {
                        const timestamp = item3.values[0]['deadline'];
                        a = { ...a, due_date: timestamp };
                    }
                }
            })
        })
        return a;
    }

    saveFilterLocally(filter: Filters) {
        this.databaseService.filters.put(filter);
    }

    async saveFiltersLocallyBulk(filters: Filters[]): Promise<void> {
        await this.databaseService.filters.bulkPut(filters);
    }


    async deleteFilterLocally(filterId: number): Promise<void> {
        await this.databaseService.filters.delete(filterId).then();
    }

    createFilterViaAPI(payload: CreateFilterPayload): Observable<Filters> {
        const requestURL = `${environment.apiURL}filter/group`;
        const options = this.httpService.createOptions(
            HttpRequestMethod.post,
            this.httpService.createHeader(),
            requestURL,
            payload,
            false
        );
        return this.httpService.execute(options).pipe(
            pluck('results'),
            map((item) => {
                const res = item as SuccessApiResponse<Filters>;
                return res.results;
            })
        );
    }

    updateFilterViaAPI(filterGroupId: number, payload: UpdateFilterPayload): Observable<Filters> {
        const requestURL = `${environment.apiURL}filter/group/${filterGroupId}`;
        const options = this.httpService.createOptions(
            HttpRequestMethod.put,
            this.httpService.createHeader(),
            requestURL,
            payload,
            false
        );
        return this.httpService.execute(options).pipe(
            pluck('results'),
            map((item) => {
                const res = item as SuccessApiResponse<Filters>;
                return res.results;
            })
        );
    }

    activateFilterForModuleViaAPI(filterGroupId: number, currentModule: string): Observable<any> {
        const requestURL = `${environment.apiURL}filter/group/${filterGroupId}/activate/module/${currentModule}`;
        const options = this.httpService.createOptions(
            HttpRequestMethod.put,
            this.httpService.createHeader(),
            requestURL,
            null,
            false
        );
        return this.httpService.execute(options).pipe(
            pluck('results')
        );
    }

    deactivateFilterForModuleViaAPI(currentModule: string): Observable<any> {
        // Correct endpoint: PUT /filter/module/<module>/deactivate
        const requestURL = `${environment.apiURL}filter/module/${currentModule}/deactivate`;
        const options = this.httpService.createOptions(
            HttpRequestMethod.put,
            this.httpService.createHeader(),
            requestURL,
            null,
            false
        );
        return this.httpService.execute(options).pipe(
            pluck('results')
        );
    }

    getAllFiltersViaAPI(): Observable<Filters[]> {
        const requestURL = `${environment.apiURL}filter/group`;
        const options = this.httpService.createOptions(
            HttpRequestMethod.get,
            this.httpService.createHeader(),
            requestURL,
            null,
            false
        );
        return this.httpService.execute(options).pipe(
            pluck('results'),
            map((item) => {
                const res = item as SuccessApiResponse<Filters[]>;
                return res.results;
            })
        );
    }

    getFilterByIdViaAPI(filterGroupId: number): Observable<Filters> {
        const requestURL = `${environment.apiURL}filter/group/${filterGroupId}`;
        const options = this.httpService.createOptions(
            HttpRequestMethod.get,
            this.httpService.createHeader(),
            requestURL,
            null,
            true
        );
        return this.httpService.execute(options).pipe(
            pluck('results'),
            map((item) => {
                const res = item as SuccessApiResponse<Filters>;
                return res.results;
            })
        );
    }

    getActiveFilterForModuleViaAPI(currentModule: string): Observable<Filters | null> {
        const requestURL = `${environment.apiURL}filter/group/active/${currentModule}`;
        const options = this.httpService.createOptions(
            HttpRequestMethod.get,
            this.httpService.createHeader(),
            requestURL,
            null,
            false
        );
        return this.httpService.execute(options).pipe(
            pluck('results'),
            map((item) => {
                const res = item as SuccessApiResponse<Filters | null>;
                return res.results;
            })
        );
    }

    showSnackbar(message: string, type: 'failure' | 'success'): void {
        this.snackBarService.openAlert({ message, type });
    }
}